import { useEffect, useRef, useState } from "react";
import { useI18n } from "@/lib/i18n.jsx";
import { NotebookPen, Plus, X, Check, Share2, ChevronDown, ChevronUp, CalendarDays } from "lucide-react";
import api from "@/lib/api";
import { useSWR, invalidateSWR } from "@/lib/swr";
import { toast } from "sonner";
import { Link } from "react-router-dom";

/**
 * DayNotesCard — the "دفتر اليوم" section on the dashboard. Owns two
 * things scoped to the currently-selected date:
 *
 *   • Journal — a free-text textarea, auto-saved 800 ms after the user
 *     stops typing so we never lose thoughts to an accidental navigation.
 *
 *   • Tasks — a simple checkable list; add / toggle / delete. Persisted
 *     via a debounced write to the same PUT /notes endpoint.
 *
 * (Meal-ingredients checklist used to live here too — replaced by the
 * dedicated "قائمة التسوق" tab under Recipes, which is more flexible.)
 *
 * Sharing uses the native Web Share API against a formatted plain-text
 * payload — no image render, no public URL. On unsupported browsers we
 * fall back to copying the same payload to the clipboard.
 */

const emptyDraft = () => ({
  journal_text: "",
  tasks: [],
});

export default function DayNotesCard({ date, dayLabel }) {
  const { lang } = useI18n();
  const key = `notes:${date}`;
  const { data } = useSWR(key, async () => {
    const { data } = await api.get(`/notes?date=${date}`);
    return data;
  });

  // Collapse state — persisted per browser so a user who prefers a compact
  // dashboard doesn't have to close the card on every visit. Default open
  // so first-time users see the feature exists.
  const [open, setOpen] = useState(() => {
    try { return localStorage.getItem("dayNotes.open") !== "0"; } catch { return true; }
  });
  const toggleOpen = () => {
    setOpen((v) => {
      const next = !v;
      try { localStorage.setItem("dayNotes.open", next ? "1" : "0"); } catch {}
      return next;
    });
  };

  // Draft is what the UI edits; SWR data is the last confirmed server state.
  const [draft, setDraft] = useState(data || emptyDraft());
  useEffect(() => { if (data) setDraft(data); }, [data]);

  // Debounced persistence — flush 800 ms after the last change so a burst
  // of typing / checkbox taps results in a single write. Empty/whitespace-
  // only tasks are the local-only "اكتب مهمتك..." placeholders the user
  // created but hasn't typed into yet — we strip them at save time so
  // the calendar/agenda never counts unwritten drafts as real tasks.
  const timer = useRef(null);
  const scheduleSave = (next) => {
    setDraft(next);
    if (timer.current) clearTimeout(timer.current);
    timer.current = setTimeout(async () => {
      const kept = (next.tasks || []).filter((t) => (t.text || "").trim());
      try {
        await api.put(`/notes?date=${date}`, {
          journal_text: next.journal_text || "",
          tasks: kept,
        });
        invalidateSWR(key);
      } catch { /* fail silently — user's local draft still shows */ }
    }, 800);
  };
  useEffect(() => () => { if (timer.current) clearTimeout(timer.current); }, []);

  // ── Task helpers ─────────────────────────────────────────────────────
  const addTask = () => {
    const next = {
      ...draft,
      tasks: [...(draft.tasks || []), { id: crypto.randomUUID?.() || String(Date.now()), text: "", done: false }],
    };
    scheduleSave(next);
  };
  const editTask = (i, patch) => {
    const tasks = [...(draft.tasks || [])];
    tasks[i] = { ...tasks[i], ...patch };
    scheduleSave({ ...draft, tasks });
  };
  const removeTask = (i) => {
    const tasks = [...(draft.tasks || [])];
    tasks.splice(i, 1);
    scheduleSave({ ...draft, tasks });
  };

  // ── Share ────────────────────────────────────────────────────────────
  const buildShareText = () => {
    const lines = [lang === "en" ? `📓 Day Journal — ${dayLabel || date}` : `📓 دفتر اليوم — ${dayLabel || date}`];
    if (draft.journal_text?.trim()) lines.push("", "📝 " + draft.journal_text.trim());
    const tasks = (draft.tasks || []).filter((t) => (t.text || "").trim());
    if (tasks.length) {
      lines.push("", lang === "en" ? "✅ Today's tasks:" : "✅ قائمة مهام اليوم:");
      tasks.forEach((t) => lines.push(`${t.done ? "☑" : "☐"} ${t.text}`));
    }
    return lines.join("\n");
  };
  const doShare = async () => {
    const text = buildShareText();
    try {
      if (navigator.share) {
        await navigator.share({ text, title: lang === "en" ? `Day Journal — ${dayLabel || date}` : `دفتر اليوم — ${dayLabel || date}` });
      } else {
        await navigator.clipboard.writeText(text);
        toast.success(lang === "en" ? "Notes copied" : "تم نسخ الملاحظات");
      }
    } catch { /* user cancelled */ }
  };

  return (
    <div className="bg-transparent rounded-3xl p-5" data-testid="day-notes-card">
      <div className="flex items-center justify-between mb-4">
        <button
          type="button"
          onClick={toggleOpen}
          className="flex items-center gap-2 flex-1 text-right group"
          data-testid="day-notes-toggle-btn"
          aria-expanded={open}
        >
          <NotebookPen className="w-5 h-5 text-brand-500" />
          <h3 className="font-black text-lg">{lang === "en" ? "Day Journal" : "دفتر اليوم"}</h3>
          {open ? (
            <ChevronUp className="w-4 h-4 text-neutral-400 group-hover:text-neutral-600 transition" />
          ) : (
            <ChevronDown className="w-4 h-4 text-neutral-400 group-hover:text-neutral-600 transition" />
          )}
        </button>
        {open && (
          <div className="flex items-center gap-2">
            <Link
              to="/app/calendar"
              className="flex items-center gap-1 text-xs text-brand-600 bg-brand-50 hover:bg-brand-100 px-3 py-1.5 rounded-full active:scale-95 transition"
              data-testid="day-notes-calendar-btn"
            >
              <CalendarDays className="w-3.5 h-3.5" /> {lang === "en" ? "Calendar" : "التقويم"}
            </Link>
            <button
              type="button"
              onClick={doShare}
              className="flex items-center gap-1 text-xs text-neutral-600 bg-neutral-50 hover:bg-neutral-100 px-3 py-1.5 rounded-full"
              data-testid="day-notes-share-btn"
            >
              <Share2 className="w-3.5 h-3.5" /> {lang === "en" ? "Share" : "مشاركة"}
            </button>
          </div>
        )}
      </div>

      {open && (<>
      {/* Journal ─────────────────────────────────────────────────────── */}
      <section className="bg-brand-50/60 rounded-2xl p-4" data-testid="day-notes-section-journal">
        <label className="text-sm font-bold text-neutral-800 block mb-1.5">{lang === "en" ? "My Journal" : "يومياتي"}</label>
        <textarea
          value={draft.journal_text || ""}
          onChange={(e) => scheduleSave({ ...draft, journal_text: e.target.value })}
          placeholder={lang === "en" ? "How was your day? Your mood, notes about hunger, energy..." : "أيش صار في يومك؟ كيف حالتك النفسية؟ ملاحظاتك عن جوعك، طاقتك، مزاجك..."}
          rows={3}
          className="w-full bg-white border border-brand-100 rounded-2xl px-3 py-2.5 text-sm resize-none focus:outline-none focus:border-brand-300"
          data-testid="day-notes-journal"
        />
      </section>

      {/* Tasks ───────────────────────────────────────────────────────── */}
      <section className="bg-brand-50/60 rounded-2xl p-4 mt-3" data-testid="day-notes-section-tasks">
        <div className="flex items-center justify-between mb-2">
          <label className="text-sm font-bold text-neutral-800">{lang === "en" ? "Today's tasks" : "قائمة مهام اليوم"}</label>
          <button
            type="button"
            onClick={addTask}
            className="flex items-center gap-1 text-xs text-brand-600 font-semibold"
            data-testid="day-notes-add-task-btn"
          >
            <Plus className="w-3.5 h-3.5" /> {lang === "en" ? "Add" : "إضافة"}
          </button>
        </div>
        {(draft.tasks || []).length === 0 ? (
          <div className="text-xs text-neutral-400 py-2">{lang === "en" ? "No tasks — add one to track your day" : "لا توجد مهام — أضف واحدة لتتبع يومك"}</div>
        ) : (
          <div className="space-y-2">
            {(draft.tasks || []).map((t, i) => (
              <div key={t.id || i} className="flex items-center gap-2" data-testid={`day-notes-task-${i}`}>
                <button
                  type="button"
                  onClick={() => editTask(i, { done: !t.done })}
                  className={`w-5 h-5 rounded-md border-2 flex-shrink-0 flex items-center justify-center transition ${t.done ? "bg-brand-500 border-brand-500" : "border-neutral-300"}`}
                  data-testid={`day-notes-task-toggle-${i}`}
                >
                  {t.done && <Check className="w-3.5 h-3.5 text-white" strokeWidth={3} />}
                </button>
                <input
                  value={t.text || ""}
                  onChange={(e) => editTask(i, { text: e.target.value })}
                  placeholder={lang === "en" ? "Write your task..." : "اكتب مهمتك..."}
                  className={`flex-1 bg-transparent text-sm border-b border-transparent focus:border-brand-300 focus:outline-none py-1 ${t.done ? "line-through text-neutral-400" : ""}`}
                  data-testid={`day-notes-task-text-${i}`}
                />
                <button
                  type="button"
                  onClick={() => removeTask(i)}
                  className="text-neutral-300 hover:text-red-500 flex-shrink-0"
                  data-testid={`day-notes-task-delete-${i}`}
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            ))}
          </div>
        )}
      </section>
      </>)}
    </div>
  );
}

